import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { TextInput } from '@hivebrite/design-system/Inputs';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';

import type { ButtonLinkProps } from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ButtonLink';

import RadiosInput from './RadiosInput';
import InputTitle from '../SettingsDecoration/InputTitle';

const EMPTY_STRING = '';
const MAIL_LINK_PREFIX = 'mailto:';
const getType = (value) =>
  value.startsWith(MAIL_LINK_PREFIX) ? 'email' : 'web';
const removeMailto = (value) => value.replace(MAIL_LINK_PREFIX, EMPTY_STRING);

type RadioLinkInputProps = Pick<
  ButtonLinkProps,
  'placeholder' | 'withToolTipDescription'
> & {
  id: string;
  label: string;
  onChange: (args: { value: string }) => void;
  value?: string;
};

const RadioLinkInput = ({
  id,
  label,
  onChange,
  placeholder,
  value,
  withToolTipDescription,
}: RadioLinkInputProps) => {
  const [inputError, setInputError] = useState();
  const [inputValue, setInputValue] = useState(value || EMPTY_STRING);
  const [inputType, setInputType] = useState(getType(inputValue));

  useEffect(() => {
    onChange({ value: inputValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleChangeInput = (e) => {
    setInputError(undefined);

    const newValue = e?.target?.value || EMPTY_STRING;

    setInputValue((inputValue) =>
      inputType === 'web' ||
      // Force email input to start with `mailto:`
      (inputType === 'email' && newValue.startsWith(MAIL_LINK_PREFIX))
        ? newValue
        : inputType === 'email' && !newValue
        ? MAIL_LINK_PREFIX
        : inputValue,
    );
  };

  const handleChangeType = (type) => {
    setInputType(type);

    if (type === 'email' && !inputValue.startsWith(MAIL_LINK_PREFIX)) {
      setInputValue((inputValue) => `mailto:${inputValue}`);
    }

    if (type === 'web' && inputValue.startsWith(MAIL_LINK_PREFIX)) {
      setInputValue((inputValue) => removeMailto(inputValue));
    }
  };

  return (
    <LinkInputWrapper>
      <InputTitle
        boxProps={{ mb: 3 }}
        id={`link-type-${id}`}
        label={__('Link to')}
      />
      <Box mb={4}>
        <RadiosInput
          direction="vertical"
          name={`link-type-${id}`}
          options={[
            {
              value: 'web',
              label: __('Web address'),
            },
            {
              value: 'email',
              label: __('Email address'),
            },
          ]}
          onChange={handleChangeType}
          value={inputType}
        />
      </Box>
      <Box
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        width="100%"
      >
        {withToolTipDescription ? (
          <Box display="-webkit-box" mr={8}>
            <InputTitle boxProps={{ mr: 1 }} id={id} label={label} />
            <Tooltip
              childrenElement={withToolTipDescription}
              initialPosition={POSITIONS['BOTTOM-RIGHT']}
            >
              <FontAwesomeIcon
                iconName="circle-info"
                iconType={CATEGORIES.SOLID}
                color="primary"
                height="16px"
              />
            </Tooltip>
          </Box>
        ) : (
          <InputTitle boxProps={{ mr: 5 }} id={id} label={label} />
        )}

        <TextInput
          clearable={
            (inputType === 'email' && inputValue !== MAIL_LINK_PREFIX) ||
            (inputType === 'web' && Boolean(inputValue))
          }
          dataTestId="builder-radio-link-input"
          error={inputError}
          id={id}
          name={id}
          onChange={handleChangeInput}
          placeholder={placeholder}
          value={inputValue}
          width="100%"
        />
      </Box>
    </LinkInputWrapper>
  );
};

export default RadioLinkInput;

const LinkInputWrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  width: '100%',
}))``;
